import { SignatureCard } from '../components/SignatureCard';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { Fingerprint, KeyRound, Loader2, Save, UserRound } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { useUpdateProfile } from '../api/update-profile';
import { PasskeysDialog } from '../components/PasskeysDialog';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const MAX_NAME_LENGTH = 200;

/**
 * The signed-in user's own account settings.
 *
 * Distinct from the Organization page, which is about the workspace and is
 * permission-gated. Everything here is about *you*, so there is no permission
 * guard — every signed-in user may edit their own profile.
 */
export function SettingsPage() {
  const setPageHeader = usePageHeaderSetter();

  useEffect(() => {
    setPageHeader({
      title: 'Settings',
      description: 'Your account',
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <div className="w-full max-w-2xl space-y-6">
        <ProfileCard />
        <SecurityCard />
        <SignatureCard />
      </div>
    </div>
  );
}

function ProfileCard() {
  const { user, refreshUser } = useAuth();
  const updateProfile = useUpdateProfile();
  const [name, setName] = useState('');

  // Seed once the user arrives, and re-seed if it changes underneath us — the
  // provider loads asynchronously, so the first render commonly has no user.
  useEffect(() => {
    if (user) {
      setName(user.name);
    }
  }, [user]);

  const trimmed = name.trim();
  const isDirty = !!user && trimmed !== user.name;
  const isValid = trimmed.length > 0 && trimmed.length <= MAX_NAME_LENGTH;

  const handleSave = async () => {
    if (!isDirty || !isValid) return;
    try {
      await updateProfile.mutateAsync({ name: trimmed });
      // The sidebar, the account menu and the avatar initials all read from
      // AuthContext, so nothing on screen changes until it re-reads.
      await refreshUser();
      toast.success('Name updated');
    } catch {
      toast.error('Could not update your name. Please try again.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <UserRound className="h-5 w-5" />
          </div>
          <div>
            <CardTitle className="text-base">Profile</CardTitle>
            <CardDescription>How your name appears to your team</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="profile-name">Name</Label>
          <Input
            id="profile-name"
            value={name}
            autoComplete="name"
            maxLength={MAX_NAME_LENGTH}
            placeholder="Your full name"
            onChange={(e) => setName(e.target.value)}
          />
          {isDirty && !isValid && <p className="text-sm text-destructive">Enter a name.</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="profile-email">Email</Label>
          <Input id="profile-email" disabled value={user?.email ?? ''} />
          <p className="text-sm text-muted-foreground">
            Your email is how you sign in and can&rsquo;t be changed here. Ask an administrator if it
            needs to move.
          </p>
        </div>

        <div className="flex justify-end">
          <Button disabled={!isDirty || !isValid || updateProfile.isPending} onClick={handleSave}>
            {updateProfile.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {updateProfile.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SecurityCard() {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passkeysOpen, setPasskeysOpen] = useState(false);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">Security</CardTitle>
              <CardDescription>How you sign in to MohairNotes</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {/* The same dialogs the account menu opens — one implementation, two
              entry points, rather than a second copy that can drift. */}
          <Button variant="outline" onClick={() => setChangePasswordOpen(true)}>
            <KeyRound className="h-4 w-4" />
            Change password
          </Button>
          <Button variant="outline" onClick={() => setPasskeysOpen(true)}>
            <Fingerprint className="h-4 w-4" />
            Passkeys
          </Button>
        </CardContent>
      </Card>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      <PasskeysDialog open={passkeysOpen} onOpenChange={setPasskeysOpen} />
    </>
  );
}
